import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import '../asset/css/Certificate.css';

export default function Certificate() {
  const { id: routeId } = useParams(); // URL မှ ID ကို ဖတ်ယူရန်
  const [searchId, setSearchId] = useState(routeId || '');
  const [result, setResult] = useState(null);
  const [searched, setSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // XSS Validation function (Allows only A-Z, a-z, 0-9, and common safe punctuation like hyphens)
  const isValidInput = (inputStr) => {
    const regex = /^[a-zA-Z0-9\-_]+$/;
    return regex.test(inputStr);
  };

  // Function to perform API request to check certificate
  const verifyCertificate = async (certId) => {
    if (!certId.trim()) return;

    // XSS Protection Check
    if (!isValidInput(certId.trim())) {
      alert('XSS detected');
      return;
    }

    setLoading(true);
    setSearched(false);
    try {
      const response = await fetch(`https://mmao.vercel.app/check/certificate/${encodeURIComponent(certId.trim())}`);
      const resJson = await response.json();
      
      if (resJson.status === 'success' && resJson.data) {
        setResult(resJson.data);
      } else {
        setResult(null);
      }
    } catch (err) {
      console.error(err);
      setResult(null);
    } finally {
      setLoading(false);
      setSearched(true);
      setCopied(false);
    }
  };

  // URL တွင် ID ပါလာပါက ဝင်ရောက်လာသည်နှင့် XSS စစ်ဆေးပြီးမှ API လှမ်းခေါ်မည်
  useEffect(() => {
    if (routeId) {
      setSearchId(routeId);
      if (!isValidInput(routeId.trim())) {
        alert('XSS detected');
      } else {
        verifyCertificate(routeId);
      }
    }
  }, [routeId]);

  // Handle Search button click
  const handleCheck = (e) => {
    e.preventDefault();
    verifyCertificate(searchId);
  };

  // Handle Copy Link action
  const handleCopyLink = () => {
    const shareUrl = `${window.location.origin}/certificate/${searchId}`;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 3000);
  };

  // Handle Print action
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="cert-container">
      <div className="cert-wrapper">
        
        {/* Header */}
        <div className="cert-header">
          <h1>Certificate Verification</h1>
          <p>Enter your Certificate ID below to authenticate and check your course credentials.</p>
        </div>

        {/* Search Card */}
        <div className="cert-card">
          <form onSubmit={handleCheck}>
            <div>
              <label htmlFor="certificateId" className="form-label">
                Certificate ID
              </label>
              <div className="input-group">
                <input
                  type="text"
                  id="certificateId"
                  value={searchId}
                  onChange={(e) => setSearchId(e.target.value)}
                  placeholder="e.g. CERT-001 or 009"
                  className="cert-input"
                />
                <button type="submit" className="btn-search" disabled={loading}>
                  {loading ? 'Checking...' : 'Check'}
                </button>
              </div>
            </div>
          </form>

          {/* Quick test chips */}
          <div className="sample-chips">
            <span>Try sample IDs:</span>
            <button type="button" onClick={() => { setSearchId('CERT-001'); verifyCertificate('CERT-001'); }} className="chip">
              CERT-001
            </button>
            <button type="button" onClick={() => { setSearchId('CERT-002'); verifyCertificate('CERT-002'); }} className="chip">
              CERT-002
            </button>
          </div>
        </div>

        {/* Loading Indicator */}
        {loading && (
          <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '20px' }}>Verifying certificate from server...</p>
        )}

        {/* Results Section */}
        {searched && !loading && (
          <div>
            {result ? (
              <div className="result-card">
                
                {/* Result Header Banner */}
                <div className="result-header">
                  <div className="result-title-group">
                    <span className="valid-text">Valid Certificate Verified</span>
                  </div>
                  <span className="badge-grade">
                    Grade: {result.grade}
                  </span>
                </div>

                {/* Data Fields */}
                <div className="result-body">
                  
                  {/* ၁။ ဖန်သားပြင် (Screen View) တွင် ပေါ်မည့် မူလဇယားအချက်အလက်များ */}
                  <div className="grid-details">
                    <div className="detail-item">
                      <span className="detail-label">Display Name</span>
                      <span className="detail-value highlight-name">{result.displayName}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Email</span>
                      <span className="detail-value">{result.email}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Course</span>
                      <span className="detail-value highlight-course">{result.course}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Certificate ID</span>
                      <span className="detail-value mono-text">{result.certificateId}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Scores</span>
                      <span className="detail-value">Assignment: <b>{result.assignmentScore}</b> | Exam: <b>{result.examScore}</b></span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Dates</span>
                      <span className="detail-value small-text">Qualified: {result.qualifyDate}</span>
                    </div>
                  </div>

                  {/* ၂။ ပရင့်ထုတ် / PDF သိမ်းသည့်အခါမှသာ ပေါ်လာမည့် ဆာတီဖိတ်ဒီဇိုင်းပုံစံ */}
                  <div className="printable-certificate">
                    <div className="cert-border-frame">
                      <div className="cert-top-row">
                        <div className="cert-logo-text">MMao Learning</div>
                        <div className="cert-badge-icon">
                            <img src='https://png.pngtree.com/png-clipart/20220604/original/pngtree-seal-gold-certificate-png-image_7931463.png' alt="Seal" style={{ width: '50px', height: '50px' }} />
                        </div>
                      </div>

                      <div className="cert-title-section">
                        <h2>CERTIFICATE OF COMPLETION</h2>
                        <p>This is to certify that</p>
                      </div>

                      <div className="cert-recipient-name">
                        {result.displayName}
                      </div>
                      <hr className="cert-divider-line"/>

                      <div className="cert-description-text">
                        has successfully completed <strong>{result.course}</strong> with Grade <strong>{result.grade}</strong> from MMao Learning and has completed all the required criteria.
                      </div>

                      <div className="cert-footer-row">
                        <div className="cert-footer-left">
                          <div className="cert-seal-badge">Official Seal</div>
                          <span>Issued Date: {result.qualifyDate || 'N/A'}</span>
                        </div>
                        <div className="cert-footer-center">
                          <p className="director-signature">Aon Mao</p>
                          <hr className="signature-line"/>
                          <span className="director-title">MR. AON MAO</span>
                          <span className="director-sub">Director - MMao Learning</span>
                        </div>
                        <div className="cert-footer-right">
                          <span className="web-url">www.mmao.site</span>
                          <span className="cert-id-print">Certificate Id: {result.certificateId}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="result-footer-info">
                    <span>Generated: {result.time || 'N/A'}</span>
                    <span className="mono-text">Doc ID: {result.id}</span>
                  </div>

                  {/* Action Buttons */}
                  <div className="action-buttons">
                    <button onClick={handlePrint} className="btn-print">
                      Print / PDF
                    </button>
                    <button onClick={handleCopyLink} className="btn-copy">
                      {copied ? 'Link Copied!' : 'Copy Link'}
                    </button>
                  </div>

                </div>
              </div>
            ) : (
              <div className="not-found-card">
                <h3>Certificate Not Found</h3>
                <p>
                  No certificate matching ID &quot;<span className="mono-text">{searchId}</span>&quot; exists in our database. Please check the ID and try again.
                </p>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}